import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  useColorModeValue,
  Divider,
  Icon,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { FiShield, FiLock, FiUserCheck, FiDatabase } from 'react-icons/fi';

const Section = ({ icon, title, children }) => (
  <Box 
    w="full" 
    bg={useColorModeValue('white', 'gray.800')} 
    p={6} 
    borderRadius="lg" 
    shadow="md"
    transition="all 0.3s"
    _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
  >
    <HStack spacing={4} mb={4}>
      <Icon as={icon} w={6} h={6} color="brand.500" />
      <Heading size="md">{title}</Heading>
    </HStack>
    {children}
  </Box>
);

const PrivacyPolicy = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');

  return (
    <Box bg={bgColor} minH="100vh" py={12}>
      <Container maxW="container.md">
        <VStack spacing={8} align="start">
          <VStack align="start" spacing={3} w="full">
            <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
              Legal Document
            </Badge>
            <Heading size="xl" color={useColorModeValue('gray.800', 'white')}>
              Privacy Policy
            </Heading>
            <Text color="gray.500">
              Last updated: {new Date().toLocaleDateString()}
            </Text>
            <Divider my={4} borderColor={useColorModeValue('gray.200', 'gray.700')} />
          </VStack>

          <VStack spacing={6} w="full">
            <Section icon={FiDatabase} title="Information We Collect">
              <UnorderedList spacing={3} stylePosition="inside" pl={6}>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Personal Information</Text>
                  <Text mt={1}>Name, email address, phone number, and other contact details</Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Professional Information</Text>
                  <Text mt={1}>Business details, professional qualifications, and experience</Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Financial Information</Text>
                  <Text mt={1}>Transaction history, commission details, and payment information</Text>
                </ListItem>
                <ListItem>
                  <Text fontWeight="medium" color="brand.500">Usage Data</Text>
                  <Text mt={1}>Platform interaction data, preferences, and analytics</Text>
                </ListItem>
              </UnorderedList>
            </Section>

            <Section icon={FiUserCheck} title="How We Use Your Information">
              <UnorderedList spacing={3} stylePosition="inside" pl={6}>
                <ListItem>Service delivery and maintenance</ListItem>
                <ListItem>Communication about platform updates and changes</ListItem>
                <ListItem>Customer support and issue resolution</ListItem>
                <ListItem>Analytics and service improvement</ListItem>
              </UnorderedList>
            </Section>

            <Section icon={FiLock} title="Data Security">
              <Text lineHeight="tall">
                We implement industry-standard security measures to protect your personal information:
              </Text>
              <UnorderedList spacing={3} mt={4} stylePosition="inside" pl={6}>
                <ListItem>End-to-end encryption for sensitive data</ListItem>
                <ListItem>Secure server infrastructure with regular updates</ListItem>
                <ListItem>Regular security assessments and audits</ListItem>
                <ListItem>Strict access controls and authentication</ListItem>
              </UnorderedList>
            </Section>

            <Section icon={FiShield} title="Your Rights">
              <Text lineHeight="tall" mb={4}>
                You have several rights regarding your personal information:
              </Text>
              <Box 
                p={4} 
                bg={useColorModeValue('gray.50', 'gray.700')} 
                borderRadius="md"
              >
                <UnorderedList spacing={3} stylePosition="inside" pl={6}>
                  <ListItem>Right to access your personal data</ListItem>
                  <ListItem>Right to correct inaccurate information</ListItem>
                  <ListItem>Right to request data deletion</ListItem>
                  <ListItem>Right to restrict processing</ListItem>
                  <ListItem>Right to data portability</ListItem>
                </UnorderedList>
              </Box>
            </Section>
          </VStack>
        </VStack>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy; 